function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pendingId(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const { id } = value;
  if (typeof id === "string" || typeof id === "number") {
    return String(id);
  }
  return undefined;
}

function pendingLabel(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  const { label } = value;
  return typeof label === "string" ? label : undefined;
}

function listLengthFromData(data: unknown): number | undefined {
  if (!isRecord(data)) return undefined;
  const { dealerCustomers } = data;
  return Array.isArray(dealerCustomers) ? dealerCustomers.length : undefined;
}

function rowIndexFromPath(path: unknown): number | undefined {
  if (!Array.isArray(path) || path.length < 2) return undefined;
  const index: unknown = path[1];
  return typeof index === "number" ? index : undefined;
}

export function summarizePending(
  pending: unknown[] | undefined
): string | undefined {
  if (!Array.isArray(pending) || pending.length === 0) return undefined;

  const counts = new Map<string, number>();
  for (const item of pending) {
    const label = pendingLabel(item);
    if (label === undefined) continue;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  if (counts.size === 0) return undefined;

  return [...counts.entries()]
    .map(([label, count]) => `${label}×${String(count)}`)
    .join(", ");
}

export class DeferLabelTracker {
  private readonly pendingByLabel = new Map<string, Set<string>>();
  private readonly completedIds = new Set<string>();
  private readonly settledLabels = new Set<string>();
  private readonly incrementalRowsByLabel = new Map<string, Set<number>>();
  private expectedListLength: number | undefined;

  observeInitialData(data: unknown) {
    const length = listLengthFromData(data);
    if (length !== undefined) {
      this.expectedListLength = length;
    }
  }

  observePending(pending: unknown[] | undefined) {
    if (!Array.isArray(pending)) return;

    for (const item of pending) {
      const id = pendingId(item);
      const label = pendingLabel(item);
      if (id === undefined || label === undefined) continue;

      const ids = this.pendingByLabel.get(label) ?? new Set<string>();
      ids.add(id);
      this.pendingByLabel.set(label, ids);
    }
  }

  observeIncremental(incremental: unknown[] | undefined): string[] {
    if (!Array.isArray(incremental)) return [];

    for (const item of incremental) {
      if (!isRecord(item)) continue;

      const label = pendingLabel(item);
      if (label === undefined) continue;

      const rowIndex = rowIndexFromPath(item.path);
      if (rowIndex === undefined) continue;

      const rows = this.incrementalRowsByLabel.get(label) ?? new Set<number>();
      rows.add(rowIndex);
      this.incrementalRowsByLabel.set(label, rows);
    }

    return this.checkIncrementalLabelSettlement();
  }

  observeCompleted(completed: unknown[] | undefined): string[] {
    if (!Array.isArray(completed)) return [];

    for (const item of completed) {
      const id = pendingId(item);
      if (id !== undefined) {
        this.completedIds.add(id);
      }
    }

    return this.checkPendingLabelSettlement();
  }

  private checkPendingLabelSettlement(): string[] {
    const newlySettled: string[] = [];

    for (const [label, pendingIds] of this.pendingByLabel) {
      if (this.settledLabels.has(label) || pendingIds.size === 0) continue;

      let allDone = true;
      for (const id of pendingIds) {
        if (!this.completedIds.has(id)) {
          allDone = false;
          break;
        }
      }

      if (allDone) {
        this.settledLabels.add(label);
        newlySettled.push(label);
      }
    }

    return newlySettled;
  }

  private checkIncrementalLabelSettlement(): string[] {
    if (this.expectedListLength === undefined) return [];

    const newlySettled: string[] = [];

    for (const [label, rows] of this.incrementalRowsByLabel) {
      if (this.settledLabels.has(label)) continue;
      if (rows.size >= this.expectedListLength) {
        this.settledLabels.add(label);
        newlySettled.push(label);
      }
    }

    return newlySettled;
  }

  patchCountForLabel(label: string): number {
    const pendingIds = this.pendingByLabel.get(label);
    if (pendingIds !== undefined) {
      return pendingIds.size;
    }

    const rows = this.incrementalRowsByLabel.get(label);
    return rows?.size ?? 0;
  }
}
